// The two badges a leaderboard is recognised by wherever it is listed — its
// window and its operator. Shared by the leaderboards list and the player
// card's Leaderboards table, so a board reads the same in both places.

import { formatWindowLength } from "./windowLength";
import type { Leaderboard } from "./types";

// === Window badge ==================================================== //

/**
 * Translate a cron expression into the short "weekly @ MON"-style label the
 * mockup uses. Falls back to the raw cron when it's not one of the recognised
 * weekday/monthly patterns. Pure on the cron string — no Date dependency.
 */
function describeCron(cron: string | undefined): string {
  if (!cron) return "recurring";
  const trimmed = cron.trim();
  // Standard 5-field cron: m h dom mon dow.
  const parts = trimmed.split(/\s+/);
  if (parts.length === 5) {
    const [, , dom, mon, dow] = parts;
    if (dom === "*" && mon === "*" && /^[A-Z0-6]+$/i.test(dow) && dow !== "*") {
      const map: Record<string, string> = {
        "0": "SUN", "7": "SUN",
        "1": "MON", "2": "TUE", "3": "WED", "4": "THU", "5": "FRI", "6": "SAT",
        SUN: "SUN", MON: "MON", TUE: "TUE", WED: "WED", THU: "THU", FRI: "FRI", SAT: "SAT",
      };
      const day = map[dow.toUpperCase()] ?? dow.toUpperCase();
      return `weekly @ ${day}`;
    }
    if (dow === "*" && mon === "*" && /^\d+$/.test(dom)) {
      return `monthly @ ${dom}`;
    }
    if (dow === "*" && dom === "*" && mon === "*") {
      return "daily";
    }
  }
  return trimmed;
}

/** ISO week number ("2026-W43") for an event window's start date. */
function isoWeekLabel(iso: string | undefined): string {
  if (!iso) return "event";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "event";
  // ISO week calculation (Mon = 1 .. Sun = 7).
  const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `event ${tmp.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

export interface WindowBadgeSpec {
  variant: "info" | "warn" | "neutral";
  text: string;
}

/** The fields of a board its window badge is drawn from. */
export type WindowFields = Pick<
  Leaderboard,
  "window_kind" | "cron" | "event_start_at" | "window_minutes"
>;

export function windowBadgeFor(row: WindowFields): WindowBadgeSpec {
  if (row.window_kind === "recurring") {
    return { variant: "info", text: describeCron(row.cron) };
  }
  if (row.window_kind === "event") {
    return { variant: "warn", text: isoWeekLabel(row.event_start_at) };
  }
  // Same tint as `recurring`: both boards are cut by a schedule, and what
  // tells them apart in one cell is the length written after it —
  // "weekly @ FRI · 3d" is a board that opens on Friday and shuts on Monday,
  // "weekly @ FRI" one that never shuts at all.
  //
  // The mockup draws this badge as "weekly · 3d" (§6). The schedule half is
  // shorthand there — it prints the recurring board two rows below as "weekly"
  // too, which `describeCron` has never produced for an expression naming a
  // weekday — so what is copied here is the ` · ` and the length, and the
  // schedule stays whatever the existing helper makes of the cron. A board's
  // start day is the most useful thing in that cell, not a detail to drop.
  if (row.window_kind === "recurring_event") {
    const schedule = describeCron(row.cron);
    return {
      variant: "info",
      text:
        row.window_minutes != null
          ? `${schedule} · ${formatWindowLength(row.window_minutes)}`
          : // Unreachable through the API — the column is NOT NULL on this
            // kind (lb_window_fields_check) — but a badge that prints
            // "undefined" is a worse answer than one that prints half.
            schedule,
    };
  }
  if (row.window_kind === "all_time") {
    return { variant: "neutral", text: "all-time" };
  }
  // A window kind this build has never heard of — a board created by a newer
  // server. It says so instead of being drawn as all-time: a board that resets
  // is not one that never resets, and the operator can at least read the name
  // of what they are looking at.
  return { variant: "neutral", text: row.window_kind };
}

// === Operator badge ================================================== //

/** The fields of a board its operator badge is drawn from. */
export type OperatorFields = Pick<Leaderboard, "operator" | "sort">;

export function operatorBadgeFor(row: OperatorFields): {
  glyph: string;
  label: string;
} {
  switch (row.operator) {
    case "best":
    case "worst":
      return { glyph: row.sort === "asc" ? "↑" : "↓", label: row.operator };
    case "increment":
      return { glyph: "Σ", label: "increment" };
    case "set":
      return { glyph: "=", label: "set" };
    default:
      return { glyph: "·", label: row.operator };
  }
}

/**
 * "best · higher is better" — the operator glyph spelled out, for the tooltip
 * of a glyph drawn without its label.
 */
export function operatorTitleFor(row: OperatorFields): string {
  const { label } = operatorBadgeFor(row);
  switch (row.operator) {
    case "best":
    case "worst":
      return `${label} · ${row.sort === "asc" ? "lower" : "higher"} is better`;
    case "increment":
      return `${label} · scores add up`;
    case "set":
      return `${label} · last value wins`;
    default:
      return label;
  }
}
